import { createContext, useContext, useEffect, useRef, useState, type PropsWithChildren } from 'react'

type QuickCreateAction = { id: symbol; label: string; execute: () => void }
type QuickCreateContextValue = {
  action: QuickCreateAction | null
  register: (action: QuickCreateAction) => void
  unregister: (id: symbol) => void
}

const Context = createContext<QuickCreateContextValue | null>(null)

/** A própria página fornece a ação do botão +, inclusive a autorização. */
export function QuickCreateProvider({ children }: PropsWithChildren) {
  const [action, setAction] = useState<QuickCreateAction | null>(null)
  const register = (next: QuickCreateAction) => setAction(next)
  const unregister = (id: symbol) => setAction(current => current?.id === id ? null : current)
  return <Context.Provider value={{ action, register, unregister }}>{children}</Context.Provider>
}

export function useQuickCreateAction() {
  const context = useContext(Context)
  if (!context) throw new Error('QuickCreateProvider ausente.')
  return context.action
}

export function useRegisterQuickCreate(label: string, execute: () => void, enabled: boolean) {
  const context = useContext(Context)
  if (!context) throw new Error('QuickCreateProvider ausente.')
  const callback = useRef(execute)
  callback.current = execute
  const { register, unregister } = context
  // A referência da ação permanece estável entre renders; o callback usa o estado atual.
  const handlers = useRef({ register, unregister })
  handlers.current = { register, unregister }
  useEffect(() => {
    if (!enabled) return
    const id = Symbol(label)
    handlers.current.register({ id, label, execute: () => callback.current() })
    return () => handlers.current.unregister(id)
  }, [label, enabled])
}
